/** Privát AI brief — szerkesztés, normalizálás (nem duplikálja a nyilvános hirdetés mezőit). */

export const EMPTY_AI_SUPPORT_BRIEF = {
  notes: "",
  availability: "",
  last_service: "",
  service_book: "",
  service_where: "",
  service_history: "",
  next_service: "",
  negotiable: true,
  price_floor_huf: "",
  negotiation_tone: "friendly",
  no_known_issues: false,
  cosmetic_issues: "",
  mechanical_issues: "",
  accident_notes: "",
  warranty_private: "",
  origin_notes: "",
  why_selling: "",
  viewing_address_policy: "after_agreement",
  flags: {
    garage_kept: false,
    non_smoker: false,
    trial_ride_ok: false,
    delivery_possible: false,
  },
  faq: [],
};

export const SERVICE_BOOK_OPTIONS = [
  { value: "", label: "— (a hirdetés extrái alapján)" },
  { value: "full", label: "Teljes szervizkönyv" },
  { value: "partial", label: "Részleges dokumentáció" },
  { value: "none", label: "Nincs / ismeretlen" },
];

export const SERVICE_WHERE_OPTIONS = [
  { value: "", label: "—" },
  { value: "dealer", label: "Márkaszerviz" },
  { value: "independent", label: "Független szerviz" },
  { value: "mixed", label: "Vegyes" },
];

export const NEGOTIATION_TONE_OPTIONS = [
  { value: "friendly", label: "Barátságos alku" },
  { value: "flexible", label: "Rugalmas" },
  { value: "firm", label: "Határozott (kevesebb engedmény)" },
];

export const AI_BRIEF_FLAG_DEFS = [
  { key: "garage_kept", label: "Garázsban / fedett helyen tartva" },
  { key: "non_smoker", label: "Nem dohányzó autó" },
  { key: "trial_ride_ok", label: "Próbakör / rövid tesztút megoldható" },
  { key: "delivery_possible", label: "Szállítás megbeszélhető" },
];

function normalizeFaqRow(row) {
  if (!row || typeof row !== "object") return null;
  const id = String(row.id || "").slice(0, 32);
  const answer = String(row.answer || "").trim().slice(0, 800);
  const question = String(row.question || "").slice(0, 280);
  if (!id) return null;
  return { id, question, answer };
}

function normalizeFlags(raw) {
  const base = { ...EMPTY_AI_SUPPORT_BRIEF.flags };
  if (raw && typeof raw === "object") {
    for (const k of Object.keys(base)) {
      if (raw[k] != null) base[k] = !!raw[k];
    }
  }
  return base;
}

export function normalizeAiSupportBrief(raw) {
  if (!raw || typeof raw !== "object") {
    return { ...EMPTY_AI_SUPPORT_BRIEF, faq: [], flags: { ...EMPTY_AI_SUPPORT_BRIEF.flags } };
  }
  const faq = [];
  if (Array.isArray(raw.faq)) {
    for (const row of raw.faq) {
      const n = normalizeFaqRow(row);
      if (n) faq.push(n);
    }
  }
  const tone = ["friendly", "flexible", "firm"].includes(raw.negotiation_tone)
    ? raw.negotiation_tone
    : "friendly";
  let floor = raw.price_floor_huf;
  if (floor === "" || floor == null) floor = "";
  else floor = String(Math.max(0, parseInt(floor, 10) || 0)).slice(0, 12);

  return {
    notes: String(raw.notes || "").slice(0, 4000),
    availability: String(raw.availability || "").slice(0, 1000),
    last_service: String(raw.last_service || "").slice(0, 200),
    service_book: String(raw.service_book || "").slice(0, 16),
    service_where: String(raw.service_where || "").slice(0, 16),
    service_history: String(raw.service_history || "").slice(0, 800),
    next_service: String(raw.next_service || "").slice(0, 200),
    negotiable: raw.negotiable !== false,
    price_floor_huf: floor,
    negotiation_tone: tone,
    no_known_issues: !!raw.no_known_issues,
    cosmetic_issues: String(raw.cosmetic_issues || "").slice(0, 500),
    mechanical_issues: String(raw.mechanical_issues || "").slice(0, 500),
    accident_notes: String(raw.accident_notes || "").slice(0, 500),
    warranty_private: String(raw.warranty_private || "").slice(0, 400),
    origin_notes: String(raw.origin_notes || "").slice(0, 300),
    why_selling: String(raw.why_selling || "").slice(0, 300),
    viewing_address_policy:
      raw.viewing_address_policy === "as_listed" ? "as_listed" : "after_agreement",
    flags: normalizeFlags(raw.flags),
    faq,
  };
}

export function aiSupportBriefToPayload(brief) {
  const v = normalizeAiSupportBrief(brief);
  const faq = (v.faq || []).filter((row) => (row.answer || "").trim().length > 0);
  const payload = {
    notes: v.notes,
    availability: v.availability,
    last_service: v.last_service,
    service_book: v.service_book || undefined,
    service_where: v.service_where || undefined,
    service_history: v.service_history,
    next_service: v.next_service,
    negotiable: v.negotiable,
    negotiation_tone: v.negotiation_tone,
    no_known_issues: v.no_known_issues,
    cosmetic_issues: v.cosmetic_issues,
    mechanical_issues: v.mechanical_issues,
    accident_notes: v.accident_notes,
    warranty_private: v.warranty_private,
    origin_notes: v.origin_notes,
    why_selling: v.why_selling,
    viewing_address_policy: v.viewing_address_policy,
    flags: v.flags,
  };
  if (v.price_floor_huf !== "") payload.price_floor_huf = parseInt(v.price_floor_huf, 10) || 0;
  if (faq.length) payload.faq = faq;
  return payload;
}

const TEXT_KEYS = [
  "notes",
  "availability",
  "last_service",
  "service_history",
  "next_service",
  "cosmetic_issues",
  "mechanical_issues",
  "accident_notes",
  "warranty_private",
  "origin_notes",
  "why_selling",
];

export function aiSupportBriefValid(brief) {
  const v = normalizeAiSupportBrief(brief);
  const textLen = TEXT_KEYS.reduce((n, k) => n + (v[k] || "").trim().length, 0);
  const faqLen = (v.faq || []).reduce((n, r) => n + (r.answer || "").trim().length, 0);
  const hasStructure =
    v.no_known_issues ||
    v.service_book ||
    v.service_where ||
    v.price_floor_huf !== "" ||
    Object.values(v.flags || {}).some(Boolean);
  return textLen + faqLen >= 10 || (hasStructure && (v.availability || "").trim().length >= 5);
}

/** Nyilvános hirdetésből — mit ne kérjünk újra a briefben. */
export function listingContextForAiBrief(car) {
  if (!car) return { hints: [], skipWarrantyField: false, skipServiceBookSelect: false };
  const feats = new Set(car.features || []);
  const hints = [];
  if (car.trade_in_possible) hints.push("Beszámítás / csere a hirdetésben be van jelölve");
  if (car.finance_available) hints.push("Finanszírozás jelölve a hirdetésben");
  if (car.immediate_available === false) hints.push("Előrendelhető / nem azonnal");
  if (car.warranty_type || car.warranty_until) hints.push("Garancia mező kitöltve a hirdetésen");
  if (feats.has("paper_service_book")) hints.push("Szervizkönyv extraként pipálva");
  if (feats.has("paper_keys")) hints.push("Kulcsok extraként pipálva");
  if (feats.has("paper_warranty")) hints.push("Garancia extraként pipálva");
  if (feats.has("paper_no_finance")) hints.push("Nincs hitel/zálog — extrában");
  if (car.tuv_valid_year) hints.push("Műszaki érvényesség a hirdetésben");
  return {
    hints,
    skipWarrantyField: !!(feats.has("paper_warranty") || car.warranty_type),
    skipServiceBookSelect: feats.has("paper_service_book"),
  };
}

export function aiSupportBriefCompleteness(brief, car) {
  const v = normalizeAiSupportBrief(brief);
  let score = 0;
  const max = 10;
  if ((v.availability || "").trim().length >= 8) score += 2;
  if ((v.last_service || "").trim() || (v.service_history || "").trim()) score += 1;
  if (v.no_known_issues || (v.cosmetic_issues || "").trim() || (v.accident_notes || "").trim()) score += 1;
  if (v.negotiable === false || v.price_floor_huf !== "" || v.negotiation_tone) score += 1;
  const faqFilled = (v.faq || []).filter((r) => (r.answer || "").trim()).length;
  score += Math.min(3, faqFilled);
  if ((v.warranty_private || "").trim() || listingContextForAiBrief(car).skipWarrantyField) score += 1;
  if (Object.values(v.flags || {}).some(Boolean)) score += 1;
  return { score: Math.min(max, score), max };
}
